import React, { useState } from 'react';
import { 
  Upload, 
  Download, 
  Share,
  Lock,
  Globe,
  Shield,
  FileText,
  Key,
  Users,
  Clock
} from 'lucide-react';
import ComingSoonBanner from '../common/ComingSoonBanner';

interface P2PDocument {
  id: string;
  title: string;
  type: 'medical_record' | 'lab_result' | 'prescription' | 'consent_form';
  size: string;
  ipfsHash: string;
  blockchainTxHash: string;
  isEncrypted: boolean;
  sharedWith: string[];
  createdAt: string;
  accessCount: number;
  storageLocation: 'hosted' | 'blockchain' | 'hybrid';
}

export const P2PDocumentManager: React.FC = () => {
  const [documents] = useState<P2PDocument[]>([
    {
      id: 'doc-1',
      title: 'Diabetes Lab Results - Q4 2024',
      type: 'lab_result',
      size: '2.4 MB',
      ipfsHash: 'QmXGTaGWTT1uUtfSb2sBAvkVwGxd...',
      blockchainTxHash: '0xabc123def456ghi789...',
      isEncrypted: true,
      sharedWith: ['dr.smith@medicalcenter.com'],
      createdAt: '2024-01-15T10:30:00Z',
      accessCount: 3,
      storageLocation: 'hybrid',
    },
    {
      id: 'doc-2',
      title: 'Heart Monitoring Data',
      type: 'medical_record',
      size: '15.7 MB',
      ipfsHash: 'QmYGTaGWTT1uUtfSb2sBAvkVwGxe...',
      blockchainTxHash: '0xdef456ghi789jkl012...',
      isEncrypted: true,
      sharedWith: [],
      createdAt: '2024-01-14T14:20:00Z',
      accessCount: 1,
      storageLocation: 'blockchain',
    },
  ]);

  const [storagePreference, setStoragePreference] = useState<'hosted' | 'blockchain' | 'hybrid'>('hybrid');

  const getDocumentTypeColor = (type: string) => {
    switch (type) {
      case 'medical_record': return 'bg-blue-100 text-blue-800';
      case 'lab_result': return 'bg-green-100 text-green-800';
      case 'prescription': return 'bg-purple-100 text-purple-800';
      case 'consent_form': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStorageLocationIcon = (location: string) => {
    switch (location) {
      case 'hosted': return <Shield className="h-4 w-4 text-blue-500" />;
      case 'blockchain': return <Globe className="h-4 w-4 text-purple-500" />;
      case 'hybrid': return <Users className="h-4 w-4 text-green-500" />;
      default: return <FileText className="h-4 w-4 text-gray-500" />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Coming Soon Banner */}
      <ComingSoonBanner 
        featureName="Blockchain Document Management"
        description="Your decentralized document system will provide real blockchain storage, IPFS integration, smart contract management, and peer-to-peer sharing with military-grade encryption for your health documents."
        currentPage="/blockchain"
        variant="compact"
        expectedRelease="Q3 2025"
        showPreview={true}
      />
      
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-xl font-semibold text-slate-800">P2P Document Manager</h3>
          <p className="text-slate-600 mt-1">Manage your decentralized health documents</p>
        </div>
        <button className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors">
          <Upload className="h-4 w-4" />
          <span>Upload Document</span>
        </button>
      </div>

      {/* Storage Preference */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <h4 className="text-lg font-semibold text-slate-800 mb-4">Storage Preferences</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div 
            className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
              storagePreference === 'hosted' 
                ? 'border-blue-500 bg-blue-50' 
                : 'border-slate-200 hover:border-slate-300'
            }`}
            onClick={() => setStoragePreference('hosted')}
          >
            <div className="flex items-center space-x-3 mb-2">
              <Shield className="h-6 w-6 text-blue-500" />
              <h5 className="font-medium text-slate-800">Hosted Storage</h5>
            </div>
            <p className="text-sm text-slate-600">Fast access, server-based encryption</p>
            <p className="text-xs text-green-600 mt-1">$0.05/GB/month</p>
          </div>

          <div 
            className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
              storagePreference === 'blockchain' 
                ? 'border-purple-500 bg-purple-50' 
                : 'border-slate-200 hover:border-slate-300'
            }`}
            onClick={() => setStoragePreference('blockchain')}
          >
            <div className="flex items-center space-x-3 mb-2">
              <Globe className="h-6 w-6 text-purple-500" />
              <h5 className="font-medium text-slate-800">Blockchain Storage</h5>
            </div>
            <p className="text-sm text-slate-600">Immutable, fully decentralized</p>
            <p className="text-xs text-purple-600 mt-1">Gas fees apply</p>
          </div>

          <div 
            className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
              storagePreference === 'hybrid' 
                ? 'border-green-500 bg-green-50' 
                : 'border-slate-200 hover:border-slate-300'
            }`}
            onClick={() => setStoragePreference('hybrid')}
          >
            <div className="flex items-center space-x-3 mb-2">
              <Users className="h-6 w-6 text-green-500" />
              <h5 className="font-medium text-slate-800">Hybrid Storage</h5>
            </div>
            <p className="text-sm text-slate-600">Best of both worlds</p>
            <p className="text-xs text-green-600 mt-1">Recommended</p>
          </div>
        </div>
      </div>

      {/* Document List */}
      <div className="bg-white rounded-xl border border-slate-200">
        <div className="p-6 border-b border-slate-200">
          <h4 className="text-lg font-semibold text-slate-800">Your Documents</h4>
        </div>
        <div className="divide-y divide-slate-200">
          {documents.map((doc) => (
            <div key={doc.id} className="p-6 hover:bg-slate-50 transition-colors">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <FileText className="h-5 w-5 text-slate-400" />
                    <h5 className="font-medium text-slate-800">{doc.title}</h5>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getDocumentTypeColor(doc.type)}`}>
                      {doc.type.replace('_', ' ')}
                    </span>
                    {doc.isEncrypted && <Lock className="h-4 w-4 text-green-500" />}
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm text-slate-600 mb-3">
                    <div className="flex items-center space-x-2">
                      {getStorageLocationIcon(doc.storageLocation)}
                      <span>{doc.storageLocation}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Download className="h-4 w-4" />
                      <span>{doc.size}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Users className="h-4 w-4" />
                      <span>{doc.sharedWith.length} shared</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Clock className="h-4 w-4" />
                      <span>{new Date(doc.createdAt).toLocaleDateString()}</span>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div>
                      <span className="text-xs text-slate-500">IPFS Hash:</span>
                      <code className="ml-2 text-xs bg-slate-100 px-2 py-1 rounded">
                        {doc.ipfsHash.slice(0, 20)}...
                      </code>
                    </div>
                    <div>
                      <span className="text-xs text-slate-500">Blockchain TX:</span>
                      <code className="ml-2 text-xs bg-slate-100 px-2 py-1 rounded">
                        {doc.blockchainTxHash.slice(0, 16)}...
                      </code>
                    </div>
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <button className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" title="Download">
                    <Download className="h-4 w-4" />
                  </button>
                  <button className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors" title="Share">
                    <Share className="h-4 w-4" />
                  </button>
                  <button className="p-2 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors" title="Manage Access">
                    <Key className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Access Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg p-4 border border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600">Total Documents</p>
              <p className="text-2xl font-bold text-slate-800">{documents.length}</p>
            </div>
            <FileText className="h-8 w-8 text-blue-500" />
          </div>
        </div>
        <div className="bg-white rounded-lg p-4 border border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600">Storage Used</p>
              <p className="text-2xl font-bold text-slate-800">18.1 MB</p>
            </div>
            <Upload className="h-8 w-8 text-green-500" />
          </div>
        </div>
        <div className="bg-white rounded-lg p-4 border border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600">Monthly Cost</p>
              <p className="text-2xl font-bold text-slate-800">$0.12</p>
            </div>
            <Globe className="h-8 w-8 text-purple-500" />
          </div>
        </div>
      </div>
    </div>
  );
};
